// InitiativesPanel -- the initiatives (tracked bodies of work) this session
// was part of, from the work census.
//
// Consumer + decision: an analyst reading one session, placing it -- "is this
// an appearance of a larger effort, and which one?" Complements
// RelatedWorkPanel: that panel surfaces similar PRIOR SESSIONS from shared
// file evidence; this one surfaces named INITIATIVES the census derived
// across the whole tenant window, each expandable to the initiatives that
// travel with it.
//
// The census is generation-pinned and rebuilt out-of-band. A tenant with no
// census yet renders nothing (the feature is effectively absent, not broken);
// a censused tenant whose session carries no tracked work says so explicitly.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import OverflowTooltip from "./OverflowTooltip.jsx";
import { CONTEXT_EYEBROW_CLASS } from "./SplitPage.jsx";
import { getSessionPromptPreviews } from "../lib/sessionLabels.js";
import { lookbackTimeRange } from "../lib/relatedWork/queries.js";
import DotGrid from "./DotGrid.jsx";
import {
    carrierToSessionId,
    listCensusInitiatives,
    listInitiativeBodies,
    listInitiativesForTrace,
    listRelatedInitiatives,
} from "../lib/censusClient.js";

/// Shared time domain over the listed initiatives' lifespans (first to last
/// body birth), so every row's band draws on one comparable axis. Null when
/// no row carries usable times; a single-instant domain is padded to a day so
/// bands still have width. Exported for tests.
export function lifespanDomain(initiatives) {
    let min = Infinity;
    let max = -Infinity;
    for (const i of initiatives || []) {
        for (const t of [Date.parse(i.firstBirthTime), Date.parse(i.lastBirthTime)]) {
            if (Number.isFinite(t)) {
                min = Math.min(min, t);
                max = Math.max(max, t);
            }
        }
    }
    if (!Number.isFinite(min)) return null;
    if (max - min < 1) {
        min -= 43_200_000;
        max += 43_200_000;
    }
    return { min, max };
}

/// One initiative's band geometry on the shared axis, as percentages. Null
/// when the row has no usable birth times (the band line is skipped, not
/// drawn empty). Bands keep a minimum width so a burst initiative -- born
/// and last-touched the same day -- stays visible. Exported for tests.
export function lifespanBand(initiative, domain) {
    if (!domain) return null;
    const a = Date.parse(initiative.firstBirthTime);
    if (!Number.isFinite(a)) return null;
    const b = Date.parse(initiative.lastBirthTime);
    const span = domain.max - domain.min;
    const pct = (t) => Math.min(100, Math.max(0, ((t - domain.min) / span) * 100));
    const left = pct(a);
    const right = Number.isFinite(b) ? pct(b) : left;
    const width = Math.max(right - left, 1.5);
    return { left: Math.min(left, 100 - width), width };
}

function fmtDay(ms) {
    return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/// Share of an initiative's evidence this session participates in, as
/// display copy. Exported for tests.
export function participationShare(traceEvidenceBits, evidenceBits) {
    const total = Number(evidenceBits);
    const part = Number(traceEvidenceBits);
    if (!Number.isFinite(total) || !Number.isFinite(part) || total <= 0) return "";
    const pct = Math.round((part / total) * 100);
    if (pct < 1) return "<1% of its evidence";
    return `${Math.min(pct, 100)}% of its evidence`;
}

/// The row's visual layer: the initiative's lifespan as a band on the shared
/// axis, the session's evidence share as the solid fill inside it, and a tick
/// where this session sits in that life. Fills are flat color-mix alphas.
function LifespanBand({ initiative, domain, traceTsMs, share }) {
    const band = lifespanBand(initiative, domain);
    if (!band) return null;
    const shareFrac = (() => {
        const total = Number(initiative.evidenceBits);
        const part = Number(initiative.traceEvidenceBits);
        if (!Number.isFinite(total) || !Number.isFinite(part) || total <= 0) return 0;
        return Math.min(part / total, 1);
    })();
    const tick = Number.isFinite(traceTsMs)
        ? Math.min(100, Math.max(0, ((traceTsMs - domain.min) / (domain.max - domain.min)) * 100))
        : null;
    const born = Date.parse(initiative.firstBirthTime);
    const last = Date.parse(initiative.lastBirthTime);
    const bodies = Number(initiative.bodyCount) || 0;
    return (
        <HoverTooltip
            wide
            placement="top"
            label={
                <span className="grid gap-[4px] py-[1px]">
                    <span>
                        Active {fmtDay(born)}{Number.isFinite(last) && last !== born ? ` – ${fmtDay(last)}` : ""} ·{" "}
                        {bodies} {bodies === 1 ? "body" : "bodies"} of shared content
                    </span>
                    <span className="text-[color:var(--muted)]">
                        {share ? `This trace is involved in ${share.replace(" of its evidence", "")} of its evidence` : "This trace's share is negligible"}
                        {tick !== null ? "; the marker is when this trace happened" : ""}.
                    </span>
                </span>
            }
        >
            <span className="relative block h-[5px] min-w-0 cursor-help" style={{ background: "color-mix(in srgb, var(--line-soft) 55%, transparent)" }}>
                <span
                    className="absolute inset-y-0"
                    style={{
                        left: `${band.left}%`,
                        width: `${band.width}%`,
                        background: "color-mix(in srgb, var(--green) 28%, transparent)",
                    }}
                >
                    <span className="absolute inset-y-0 left-0" style={{ width: `${shareFrac * 100}%`, background: "var(--green)" }} />
                </span>
                {tick !== null && (
                    <span
                        className="absolute top-[-2px] bottom-[-2px] w-[1px]"
                        style={{ left: `${tick}%`, background: "color-mix(in srgb, var(--ink) 70%, transparent)" }}
                    />
                )}
            </span>
        </HoverTooltip>
    );
}

/// Per-session evidence shares over one initiative's fetched bodies: a
/// body's bits count toward every session in its carrier set (the same
/// convention as the reverse lookup's trace_evidence_bits), so a session's
/// total is the evidence that flows through it. Sorted strongest first.
/// Exported for tests.
export function sessionSharesFromBodies(bodies) {
    const byCarrier = new Map();
    let total = 0;
    for (const body of bodies || []) {
        const bits = Number(body.bits) || 0;
        total += bits;
        for (const carrier of body.carriers || []) {
            byCarrier.set(carrier, (byCarrier.get(carrier) || 0) + bits);
        }
    }
    const sessions = [...byCarrier.entries()]
        .map(([carrier, bits]) => ({ carrier, bits }))
        .sort((a, b) => b.bits - a.bits || (a.carrier < b.carrier ? -1 : 1));
    return { sessions, totalBits: total };
}

const SESSION_SHARE_PREVIEW = 6;

/// The last path segment, the only label that fits inside a circle.
function subjectTail(subject) {
    const parts = String(subject || "").split("/").filter(Boolean);
    return parts[parts.length - 1] || subject;
}

/// The matrix's pure data shaping, exported for tests: one row per
/// initiative (its per-session evidence from the fetched bodies), columns =
/// the sessions carrying the most evidence across all rows, this session
/// pinned first when present. `per` is [{ subject, shares }] with `shares`
/// from [`sessionSharesFromBodies`].
export function buildInitiativeMatrix(per, { sessionId, cap = 10 } = {}) {
    const totals = new Map();
    const cells = new Map();
    const rowMax = new Map();
    const rowTotals = new Map();
    for (const { subject, shares } of per || []) {
        const rowMap = new Map();
        let max = 0;
        for (const s of shares.sessions) {
            rowMap.set(s.carrier, s.bits);
            max = Math.max(max, s.bits);
            totals.set(s.carrier, (totals.get(s.carrier) || 0) + s.bits);
        }
        cells.set(subject, rowMap);
        rowMax.set(subject, max);
        rowTotals.set(subject, shares.totalBits);
    }
    const ranked = [...totals.entries()]
        .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
        .map(([carrier]) => carrier);
    const selfCarrier = ranked.find((c) => carrierToSessionId(c) === sessionId) || null;
    const others = ranked
        .filter((c) => c !== selfCarrier)
        .slice(0, Math.max(cap - (selfCarrier ? 1 : 0), 0));
    return { carriers: selfCarrier ? [selfCarrier, ...others] : others, cells, rowMax, rowTotals };
}

/// The contrast candidates: initiatives the matrix's OTHER sessions are on
/// that this session is not, ranked by how many of those sessions carry
/// each and how strongly. `lists` is one initiatives array per neighbor
/// session (the reverse lookup's rows). Exported for tests.
export function contrastCandidates(lists, ownSubjects, cap = 5) {
    const own = new Set(ownSubjects);
    const tally = new Map();
    for (const list of lists || []) {
        for (const i of list || []) {
            if (own.has(i.subject)) continue;
            const t = tally.get(i.subject) || { subject: i.subject, hits: 0, bits: 0 };
            t.hits += 1;
            t.bits += Number(i.traceEvidenceBits) || 0;
            tally.set(i.subject, t);
        }
    }
    return [...tally.values()]
        .sort((a, b) => b.hits - a.hits || b.bits - a.bits || (a.subject < b.subject ? -1 : 1))
        .slice(0, cap)
        .map((t) => t.subject);
}

const MATRIX_ROW_H = 20;
const MATRIX_HEADER_H = 14;

/// The initiative-by-session matrix, in the contrast panel's dot-grid
/// grammar: rows are this session's initiatives, columns are the sessions
/// carrying them (this session pinned first and highlighted), and each dot
/// is the evidence that session carries inside that initiative -- so an
/// overlap between two initiatives is visible as a COLUMN with dots in both
/// rows, and the column resolves to a real session. Hover names the session
/// in the caption strip; clicking a cell or column opens its trace.
function InitiativeMatrix({ initiatives, matrix, sessionId, onOpenTrace }) {
    const [hover, setHover] = useState(null);
    const rows = useMemo(() => {
        const own = initiatives
            .filter((i) => matrix.cells.has(i.subject))
            .map((i) => ({ id: i.subject, name: subjectTail(i.subject), initiative: i }));
        // The contrast tier: initiatives the columns' sessions are on that
        // this session is not, under a padding divider, indented as
        // sub-rows. This session's column goes empty here by construction --
        // that emptiness IS the contrast.
        const contrast = (matrix.contrast || [])
            .filter((s) => matrix.cells.has(s))
            .map((s) => ({ id: s, name: subjectTail(s), kind: "sub", indent: 10 }));
        return contrast.length
            ? [...own, { id: "__contrast__", name: "not in this trace", isPadding: true }, ...contrast]
            : own;
    }, [initiatives, matrix]);
    const cols = useMemo(
        () => matrix.carriers.map((carrier) => {
            const sid = carrierToSessionId(carrier);
            const isSelf = sid !== null && sid === sessionId;
            const preview = sid ? matrix.previews?.get(sid) : null;
            return {
                id: carrier,
                // The self column's header is a marker pointing at the
                // column, not a word -- the same triangle glyph the panel's
                // expanders already use.
                name: isSelf ? "▾" : "",
                label: isSelf
                    ? "This trace"
                    : preview?.label || (sid ? sid.slice(0, 8) : "unknown trace"),
                traceId: isSelf ? null : preview?.traceId || null,
                isSelf,
            };
        }),
        [matrix, sessionId],
    );

    const cellBits = useCallback(
        (ri, ci) => matrix.cells.get(rows[ri]?.id)?.get(cols[ci]?.id) || 0,
        [matrix, rows, cols],
    );
    const cellFn = useCallback((ri, ci) => {
        if (rows[ri]?.isPadding) return {};
        const bits = cellBits(ri, ci);
        if (bits <= 0) return { placeholder: { r: 1.2, opacity: 0.16 } };
        const max = matrix.rowMax.get(rows[ri].id) || bits;
        const norm = Math.sqrt(bits / max);
        return { dot: { r: 2 + norm * 5, opacity: 0.55 + norm * 0.45 } };
    }, [cellBits, matrix, rows]);
    const cellTooltipFn = useCallback((ri, ci, row, col) => {
        const bits = cellBits(ri, ci);
        if (!row || !col || bits <= 0) return null;
        // Contrast rows have no rollup stats on hand; their fetched-body
        // total is the honest denominator.
        const total = row.initiative
            ? Number(row.initiative.evidenceBits) || 0
            : matrix.rowTotals.get(row.id) || 0;
        const pct = total > 0 ? Math.round((bits / total) * 100) : 0;
        return (
            <div className="flex flex-col gap-[2px] max-w-[240px]">
                <span className="font-medium text-[color:var(--ink)] truncate">{col.label}</span>
                <span className="text-[color:var(--muted)] truncate">{row.id}</span>
                <span className="text-[color:var(--muted)]">
                    carries {pct > 0 ? `${pct}%` : "<1%"} of this initiative&apos;s evidence
                </span>
                {col.traceId && onOpenTrace ? (
                    <span className="text-[10px] text-[color:var(--muted-soft)]">Click to open this trace</span>
                ) : null}
            </div>
        );
    }, [cellBits, matrix, onOpenTrace]);

    const handleClick = useCallback((hit) => {
        if (!hit || !onOpenTrace) return;
        const col = hit.ci >= 0 ? cols[hit.ci] : null;
        if (col?.traceId) onOpenTrace(col.traceId);
    }, [cols, onOpenTrace]);

    if (rows.filter((r) => r.initiative).length < 2 || !cols.length) return null;
    const hoverCol = hover && hover.ci >= 0 ? cols[hover.ci] : null;
    const hoverRow = hover && hover.ri >= 0 ? rows[hover.ri] : null;
    return (
        <div className="grid gap-[4px] min-w-0">
            <div style={{ height: MATRIX_HEADER_H + rows.length * MATRIX_ROW_H }} className="min-w-0">
                <DotGrid
                    rows={rows}
                    cols={cols}
                    cellW={26}
                    rowH={MATRIX_ROW_H}
                    labelW={104}
                    headerH={MATRIX_HEADER_H}
                    cellFn={cellFn}
                    cellTooltipFn={cellTooltipFn}
                    selfColIndex={cols.findIndex((c) => c.isSelf)}
                    hoveredRowIndex={hover?.ri ?? -1}
                    hoveredColIndex={hover?.ci ?? -1}
                    onHover={(hit) => setHover(hit ? { ri: hit.ri, ci: hit.ci } : null)}
                    onClick={handleClick}
                    className="h-full w-full"
                />
            </div>
            <span className="text-[9px] leading-[1.4] text-[color:var(--muted-soft)] truncate min-w-0">
                {hoverCol
                    ? `${hoverCol.label}${hoverRow ? ` · ${hoverRow.id}` : ""}${hoverCol.traceId ? " · click to open" : ""}`
                    : hoverRow
                        ? hoverRow.id
                        : "Rows are initiatives · columns are the traces carrying them · dot = that trace's evidence in the initiative"}
            </span>
        </div>
    );
}

/// The ranked sessions of one initiative, this session highlighted in place.
/// Answers "who else is on this work, and how much of it flows through this
/// session versus the others". Other sessions are labeled by their recent
/// prompt when one is known and open on click when the panel can navigate.
function SessionShares({ shares, currentSessionId, sessionCount, previews, onOpenTrace }) {
    if (!shares.sessions.length) return null;
    const maxBits = shares.sessions[0].bits;
    const shown = shares.sessions.slice(0, SESSION_SHARE_PREVIEW);
    const beyond = Math.max((Number(sessionCount) || 0) - shown.length, 0);
    return (
        <div className="grid gap-[2px] min-w-0">
            <span className="text-[9px] leading-[1.6] tracking-[0.05em] uppercase text-[color:var(--muted-soft)]">
                Traces on this initiative
            </span>
            {shown.map((s) => {
                const sessionId = carrierToSessionId(s.carrier);
                const isThis = sessionId !== null && sessionId === currentSessionId;
                const preview = sessionId ? previews?.get(sessionId) : null;
                const label = isThis
                    ? "This trace"
                    : preview?.label || (sessionId ? sessionId.slice(0, 8) : "unknown trace");
                const pct = shares.totalBits > 0 ? Math.round((s.bits / shares.totalBits) * 100) : 0;
                const canOpen = !isThis && typeof onOpenTrace === "function" && preview?.traceId;
                return (
                    <div key={s.carrier} className="flex items-center gap-[8px] min-w-0">
                        <span
                            className="relative block h-[4px] w-[36px] shrink-0"
                            style={{ background: "color-mix(in srgb, var(--line-soft) 55%, transparent)" }}
                        >
                            <span
                                className="absolute inset-y-0 left-0"
                                style={{
                                    width: `${maxBits > 0 ? Math.max((s.bits / maxBits) * 100, 3) : 0}%`,
                                    background: isThis
                                        ? "var(--green)"
                                        : "color-mix(in srgb, var(--line-strong) 85%, transparent)",
                                }}
                            />
                        </span>
                        {canOpen ? (
                            <button
                                type="button"
                                onClick={() => onOpenTrace(preview.traceId)}
                                className="min-w-0 flex-1 truncate cursor-pointer bg-transparent border-0 p-0 text-left text-[10px] leading-[1.5] text-[color:var(--muted)] hover:underline hover:text-[color:var(--ink)]"
                            >
                                {label}
                            </button>
                        ) : (
                            <OverflowTooltip
                                className={`min-w-0 flex-1 truncate text-[10px] leading-[1.5] ${isThis ? "font-medium text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}
                            >
                                {label}
                            </OverflowTooltip>
                        )}
                        <span className="text-[9px] leading-[1.5] tabular-nums text-[color:var(--muted-soft)] whitespace-nowrap shrink-0">
                            {pct}%
                        </span>
                    </div>
                );
            })}
            {beyond > 0 && (
                <span className="text-[9px] leading-[1.4] text-[color:var(--muted-soft)]">
                    +{beyond} more trace{beyond === 1 ? "" : "s"}
                </span>
            )}
        </div>
    );
}

/// Overlap strength for one related row: the shared-session fraction of the
/// smaller side, so "3 of its 4 sessions ride along" reads as a nearly full
/// bar even when the target is much larger. Exported for tests.
export function overlapFraction(row) {
    const shared = Number(row.sharedSessions);
    const smaller = Math.min(Number(row.neighborSessions), Number(row.targetSessions));
    if (!Number.isFinite(shared) || !Number.isFinite(smaller) || smaller <= 0) return 0;
    return Math.min(shared / smaller, 1);
}

function InitiativeRow({ initiative, rebuildId, domain, traceTsMs, sessionId, rank, onOpenTrace }) {
    const [expanded, setExpanded] = useState(false);
    const [detail, setDetail] = useState({ status: "idle", rows: [], shares: null, previews: null });
    // A successful fetch is kept for the row's lifetime; anything else
    // (error, or a fetch abandoned by collapsing mid-flight) refetches on the
    // next expand. Deliberately NOT keyed on `detail.status`: the fetch
    // setting its own loading state must not re-run its effect, which would
    // both drop the in-flight result via the cleanup and retry-loop a
    // persistent failure.
    const settledRef = useRef(false);

    useEffect(() => {
        if (!expanded || settledRef.current) return undefined;
        let alive = true;
        setDetail({ status: "loading", rows: [], shares: null, previews: null });
        (async () => {
            const [rel, bod] = await Promise.all([
                listRelatedInitiatives(initiative.subject, { rebuildId, pageSize: 5 }),
                listInitiativeBodies(initiative.subject, { rebuildId, pageSize: 100 }),
            ]);
            const shares = sessionSharesFromBodies(bod.bodies);
            // Label the top other-sessions by their recent prompt; a failed
            // preview read degrades to short ids, never to an error.
            const ids = shares.sessions
                .slice(0, SESSION_SHARE_PREVIEW)
                .map((s) => carrierToSessionId(s.carrier))
                .filter((id) => id && id !== sessionId);
            const previews = ids.length
                ? await getSessionPromptPreviews(ids, lookbackTimeRange()).catch(() => new Map())
                : new Map();
            if (!alive) return;
            settledRef.current = true;
            setDetail({ status: "ready", rows: rel.related, shares, previews });
        })().catch((error) => {
            if (!alive) return;
            console.warn("[initiatives] detail unavailable:", error?.message || error);
            setDetail({ status: "error", rows: [], shares: null, previews: null });
        });
        return () => { alive = false; };
        // sessionId is constant for this instance's lifetime (rows are keyed
        // by session), so the settled cache cannot go stale across sessions.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded, initiative.subject, rebuildId]);

    const share = participationShare(
        initiative.traceEvidenceBits,
        initiative.evidenceBits,
    );
    const sessions = Number(initiative.sessionCount) || 0;

    return (
        <div className="grid gap-[3px] min-w-0">
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="flex items-baseline gap-[8px] min-w-0 text-left cursor-pointer bg-transparent border-0 p-0"
            >
                <OverflowTooltip
                    className="text-[12px] leading-[1.3] font-medium text-[color:var(--ink)] truncate min-w-0"
                    tooltipContent={initiative.topLabel || initiative.subject}
                >
                    {initiative.subject}
                </OverflowTooltip>
                <span className="text-[10px] leading-[1.3] text-[color:var(--muted)] whitespace-nowrap shrink-0">
                    {sessions} trace{sessions === 1 ? "" : "s"}
                    {share ? ` · ${share}` : ""}
                    {rank ? ` · #${rank.rank} of ${rank.total}` : ""}
                </span>
            </button>
            <LifespanBand initiative={initiative} domain={domain} traceTsMs={traceTsMs} share={share} />
            {expanded && (
                <div
                    aria-busy={detail.status === "loading"}
                    className="grid gap-[6px] pl-[10px] border-l border-[color:var(--line-soft)]"
                >
                    {detail.status === "loading" && (
                        <span className="text-[10px] leading-[1.3] text-[color:var(--muted)]">
                            Reading this initiative&apos;s evidence…
                        </span>
                    )}
                    {detail.status === "error" && (
                        <span className="text-[10px] leading-[1.3] text-[color:var(--muted)]">
                            Initiative detail is unavailable. Try again later.
                        </span>
                    )}
                    {detail.status === "ready" && detail.shares && (
                        <SessionShares
                            shares={detail.shares}
                            currentSessionId={sessionId}
                            sessionCount={initiative.sessionCount}
                            previews={detail.previews}
                            onOpenTrace={onOpenTrace}
                        />
                    )}
                    {detail.status === "ready" && detail.rows.length > 0 && (
                        <span className="text-[9px] leading-[1.6] tracking-[0.05em] uppercase text-[color:var(--muted-soft)]">
                            Travels with
                        </span>
                    )}
                    {detail.status === "ready" && detail.rows.length === 0 && (
                        <span className="text-[10px] leading-[1.3] text-[color:var(--muted)]">
                            No other initiative shares enough traces with this one.
                        </span>
                    )}
                    {detail.status === "ready" &&
                        detail.rows.map((r) => (
                            <div key={r.subject} className="flex items-center gap-[8px] min-w-0">
                                <OverflowTooltip className="text-[11px] leading-[1.3] text-[color:var(--ink)] truncate min-w-0">
                                    {r.subject}
                                </OverflowTooltip>
                                <HoverTooltip
                                    placement="top"
                                    label={`${Number(r.sharedSessions) || 0} of the smaller initiative's ${Math.min(Number(r.neighborSessions) || 0, Number(r.targetSessions) || 0)} traces worked on both`}
                                >
                                    <span
                                        className="relative block h-[4px] w-[36px] shrink-0 cursor-help"
                                        style={{ background: "color-mix(in srgb, var(--line-soft) 55%, transparent)" }}
                                    >
                                        <span
                                            className="absolute inset-y-0 left-0"
                                            style={{ width: `${overlapFraction(r) * 100}%`, background: "color-mix(in srgb, var(--green) 65%, transparent)" }}
                                        />
                                    </span>
                                </HoverTooltip>
                                <span className="text-[10px] leading-[1.3] text-[color:var(--muted)] whitespace-nowrap shrink-0">
                                    {Number(r.sharedSessions) || 0} shared
                                </span>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}

/// The "what is an initiative" tooltip, exported so WorkTabs can hang it off
/// its shared tab strip.
export function initiativesTooltip() {
    return (
        <span className="grid gap-[6px] py-[2px]">
            <span className="font-medium">Initiatives</span>
            <span className="text-[color:var(--muted)]">
                Bodies of work identified from content that several traces carry
                together, named by the repository area or site they center on.
            </span>
            <span className="text-[color:var(--muted)]">
                Trace counts are initiative-wide; the percentage is how much of the
                initiative&apos;s evidence involves this trace, and the # is its
                evidence rank among every initiative in this census reading. Expand a
                row to see the traces the work flows through -- this one
                highlighted among the others -- and the initiatives that share
                traces with it.
            </span>
            <span className="text-[color:var(--muted)]">
                Each band is the initiative&apos;s lifespan on a shared time axis; the
                solid fill inside it is this trace&apos;s share of the evidence, and
                the marker is when this trace happened.
            </span>
        </span>
    );
}

// variant "section" renders the standalone bordered section; "tab" renders
// only the status/body content for a parent-owned tab strip (WorkTabs),
// which follows this panel's state through onStatus. onOpenTrace, when
// given, makes the other sessions in an expanded row's share list open on
// click.
export default function InitiativesPanel({ trace, variant = "section", onStatus = null, onOpenTrace = null }) {
    const sessionId = trace?.sessionId ? String(trace.sessionId) : null;
    const traceTsMs = trace?.timestamp instanceof Date
        ? trace.timestamp.getTime()
        : Number(trace?.timestamp) || null;
    const [state, setState] = useState({ status: "idle" });
    const [attempt, setAttempt] = useState(0);
    const runIdRef = useRef(0);

    useEffect(() => { onStatus?.(state); }, [onStatus, state]);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "hidden" });
            return undefined;
        }
        let alive = true;
        const runId = runIdRef.current + 1;
        runIdRef.current = runId;
        setState({ status: "loading" });
        listInitiativesForTrace(sessionId, { pageSize: 8 })
            .then((res) => {
                if (!alive || runId !== runIdRef.current) return;
                if (!res.rebuildId) {
                    // No completed census for this tenant: the feature is
                    // absent, not empty -- render nothing.
                    setState({ status: "hidden" });
                    return;
                }
                setState({
                    status: "ready",
                    initiatives: res.initiatives,
                    rebuildId: res.rebuildId,
                });
            })
            .catch((error) => {
                if (!alive || runId !== runIdRef.current) return;
                if (error?.status === 404) {
                    // Route not served yet (a dashboard deployed ahead of its
                    // gateway): absent, not broken -- render nothing rather
                    // than an error rail on every session.
                    setState({ status: "hidden" });
                    return;
                }
                console.warn("[initiatives] unavailable:", error?.message || error);
                setState({ status: "error" });
            });
        return () => { alive = false; };
    }, [sessionId, attempt]);

    // The matrix's data: one bounded bodies fan-out per panel, shaped into
    // per-initiative session shares plus the ranked column universe, then one
    // preview read to label the columns. A subject whose read fails simply
    // contributes an empty row; a failed preview read degrades to short ids.
    const [matrix, setMatrix] = useState(null);
    useEffect(() => {
        if (state.status !== "ready" || state.initiatives.length < 2) {
            setMatrix(null);
            return undefined;
        }
        let alive = true;
        (async () => {
            const per = await Promise.all(
                state.initiatives.map((i) =>
                    listInitiativeBodies(i.subject, { rebuildId: state.rebuildId, pageSize: 100 })
                        .then((res) => ({ subject: i.subject, shares: sessionSharesFromBodies(res.bodies) }))
                        .catch(() => ({ subject: i.subject, shares: { sessions: [], totalBits: 0 } })),
                ),
            );
            const built = buildInitiativeMatrix(per, { sessionId, cap: 10 });
            const ids = built.carriers
                .map((c) => carrierToSessionId(c))
                .filter((id) => id && id !== sessionId);
            // The contrast tier: what the matrix's OTHER sessions are on that
            // this session is not. One reverse lookup per column session,
            // pinned to the same generation, then one bodies read per
            // surviving candidate so its cells land on the same columns.
            const neighborLists = await Promise.all(
                ids.map((id) =>
                    listInitiativesForTrace(id, { rebuildId: state.rebuildId, pageSize: 8 })
                        .then((res) => res.initiatives)
                        .catch(() => []),
                ),
            );
            const contrast = contrastCandidates(
                neighborLists,
                state.initiatives.map((i) => i.subject),
            );
            const contrastPer = await Promise.all(
                contrast.map((subject) =>
                    listInitiativeBodies(subject, { rebuildId: state.rebuildId, pageSize: 100 })
                        .then((res) => ({ subject, shares: sessionSharesFromBodies(res.bodies) }))
                        .catch(() => ({ subject, shares: { sessions: [], totalBits: 0 } })),
                ),
            );
            for (const { subject, shares } of contrastPer) {
                const rowMap = new Map();
                let max = 0;
                for (const s of shares.sessions) {
                    rowMap.set(s.carrier, s.bits);
                    max = Math.max(max, s.bits);
                }
                built.cells.set(subject, rowMap);
                built.rowMax.set(subject, max);
                built.rowTotals.set(subject, shares.totalBits);
            }
            const previews = ids.length
                ? await getSessionPromptPreviews(ids, lookbackTimeRange()).catch(() => new Map())
                : new Map();
            if (!alive) return;
            setMatrix({ ...built, contrast, previews });
        })();
        return () => { alive = false; };
    }, [state, sessionId]);

    // The company-wide frame: the generation's full initiative rollup, ranked
    // by evidence, so each row can say where it sits among everything the
    // tenant is working on. Silent degrade: no ranks, same panel.
    const [ranks, setRanks] = useState(null);
    useEffect(() => {
        if (state.status !== "ready" || !state.rebuildId || !state.initiatives.length) {
            setRanks(null);
            return undefined;
        }
        let alive = true;
        listCensusInitiatives({ rebuildId: state.rebuildId, pageSize: 1000 })
            .then((res) => {
                if (!alive) return;
                const map = new Map();
                res.initiatives.forEach((i, idx) => map.set(i.subject, idx + 1));
                // A full page means the read hit its cap and the true total is
                // larger; say so rather than understating the company map.
                setRanks({
                    map,
                    total: `${res.initiatives.length}${res.initiatives.length >= 1000 ? "+" : ""}`,
                });
            })
            .catch((error) => {
                console.warn("[initiatives] rollup rank unavailable:", error?.message || error);
                if (alive) setRanks(null);
            });
        return () => { alive = false; };
    }, [state]);

    const body = useMemo(() => {
        if (state.status !== "ready") return null;
        if (!state.initiatives.length) {
            return (
                <span className="text-[11px] leading-[1.35] text-[color:var(--muted)]">
                    This trace is not part of any tracked initiative. Initiatives
                    need content shared across several traces.
                </span>
            );
        }
        const domain = lifespanDomain(state.initiatives);
        return (
            <div className="grid gap-[7px] min-w-0">
                {state.initiatives.length >= 2 && matrix !== null && (
                    <InitiativeMatrix
                        initiatives={state.initiatives}
                        matrix={matrix}
                        sessionId={sessionId}
                        onOpenTrace={onOpenTrace}
                    />
                )}
                {domain && (
                    <div className="flex items-baseline justify-between text-[9px] leading-[1.2] tabular-nums text-[color:var(--muted-soft)]">
                        <span>{fmtDay(domain.min)}</span>
                        <span className="tracking-[0.05em] uppercase">lifespan · this trace&apos;s share</span>
                        <span>{fmtDay(domain.max)}</span>
                    </div>
                )}
                {/* Rows are keyed by session as well as subject: a settled
                    row caches its expansion for its lifetime, and a session
                    change must reset that cache (the share highlight and the
                    preview lookup are session-relative), not inherit it. */}
                {state.initiatives.map((i) => (
                    <InitiativeRow
                        key={`${sessionId}:${i.subject}`}
                        initiative={i}
                        rebuildId={state.rebuildId}
                        domain={domain}
                        traceTsMs={traceTsMs}
                        sessionId={sessionId}
                        rank={ranks?.map.has(i.subject)
                            ? { rank: ranks.map.get(i.subject), total: ranks.total }
                            : null}
                        onOpenTrace={onOpenTrace}
                    />
                ))}
            </div>
        );
    }, [state, traceTsMs, sessionId, ranks, onOpenTrace, matrix]);

    if (state.status === "hidden" || state.status === "idle") return null;

    const content = state.status === "loading" ? (
        <span className="inline-flex items-center gap-[6px] text-[11px] leading-[1.2] text-[color:var(--muted)]">
            <span className="inline-block h-[7px] w-[7px] rounded-full bg-[color:var(--line-strong)] shrink-0 animate-pulse" />
            Checking tracked initiatives…
        </span>
    ) : state.status === "error" ? (
        <span className="text-[11px] leading-[1.35] text-[color:var(--muted)]">
            Initiatives are unavailable.{" "}
            <button
                type="button"
                onClick={() => setAttempt((n) => n + 1)}
                className="cursor-pointer bg-transparent border-0 p-0 text-[11px] underline decoration-dotted underline-offset-2 text-[color:var(--muted)]"
            >
                Retry
            </button>
        </span>
    ) : body;

    if (variant === "tab") {
        return (
            <div aria-busy={state.status === "loading"} className="grid gap-[8px] min-w-0">
                {content}
            </div>
        );
    }

    return (
        <div
            aria-busy={state.status === "loading"}
            className="grid gap-[8px] border-l border-[color:var(--line-soft)] pl-[12px] py-[2px] min-w-0"
        >
            <div className="flex items-baseline gap-[8px]">
                <span className={CONTEXT_EYEBROW_CLASS}>Initiatives</span>
                <HoverTooltip wide placement="top" label={initiativesTooltip()}>
                    <span className="cursor-help text-[10px] leading-[1.2] text-[color:var(--muted-soft)] underline decoration-dotted underline-offset-2">
                        details
                    </span>
                </HoverTooltip>
            </div>
            {content}
        </div>
    );
}
